import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  Mail, 
  Settings, 
  Bell, 
  ShoppingCart, 
  FileText, 
  UserCheck, 
  Package,
  Send,
  AlertCircle,
  Key,
  Save,
  RefreshCw,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  User,
  Pen,
  MessageSquare
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Email notification types with their triggers
const EMAIL_TYPES = [
  {
    slug: 'order_confirmation',
    name: 'Order Confirmation',
    description: 'Sent to customer when they place an order',
    recipient: 'Customer',
    trigger: 'Order placed (Stripe or Cash)',
    icon: ShoppingCart,
    category: 'Orders'
  },
  {
    slug: 'new_order_admin',
    name: 'New Order Notification',
    description: 'Sent to admin when a new order is received',
    recipient: 'Admin (sales@techdistribution.com.au)',
    trigger: 'Order placed',
    icon: Bell,
    category: 'Orders'
  },
  {
    slug: 'payment_confirmation',
    name: 'Payment Confirmation',
    description: 'Sent to customer when payment is successful',
    recipient: 'Customer',
    trigger: 'Stripe payment completed',
    icon: ShoppingCart,
    category: 'Orders'
  },
  {
    slug: 'order_shipped',
    name: 'Order Shipped',
    description: 'Sent to customer when order is marked as shipped',
    recipient: 'Customer',
    trigger: 'Admin ships order',
    icon: Package,
    category: 'Orders',
    userPref: 'order_updates'
  },
  {
    slug: 'estimate_sent',
    name: 'Estimate Sent',
    description: 'Sent to customer with PDF estimate attached',
    recipient: 'Customer',
    trigger: 'Admin sends estimate',
    icon: FileText,
    category: 'Estimates',
    userPref: 'estimate_reminders'
  },
  {
    slug: 'estimate_approved_admin',
    name: 'Estimate Approved',
    description: 'Sent to admin when customer approves an estimate',
    recipient: 'Admin (sales@techdistribution.com.au)',
    trigger: 'Customer approves estimate',
    icon: FileText,
    category: 'Estimates'
  },
  {
    slug: 'invoice',
    name: 'Invoice',
    description: 'Sent to customer with invoice PDF',
    recipient: 'Customer',
    trigger: 'Admin sends invoice',
    icon: FileText,
    category: 'Invoices',
    userPref: 'invoice_reminders'
  },
  {
    slug: 'ticket_reply',
    name: 'Ticket Reply',
    description: 'Sent to customer when admin replies to their ticket (manual replies only, not email sync)',
    recipient: 'Customer',
    trigger: 'Admin sends manual reply',
    icon: MessageSquare,
    category: 'Tickets',
    userPref: 'ticket_updates',
    loopSafe: true
  },
  {
    slug: 'ticket_status_change',
    name: 'Ticket Status Change',
    description: 'Sent to customer when ticket status changes',
    recipient: 'Customer',
    trigger: 'Admin changes ticket status',
    icon: MessageSquare,
    category: 'Tickets',
    userPref: 'ticket_updates',
    loopSafe: true
  },
  {
    slug: 'backorder_ready',
    name: 'Backorder Ready',
    description: 'Sent to customer when backordered items are ready',
    recipient: 'Customer',
    trigger: 'Admin marks backorder ready',
    icon: Package,
    category: 'Orders',
    userPref: 'backorder_updates'
  },
  {
    slug: 'trade_approved',
    name: 'Trade Application Approved',
    description: 'Sent when a trade application is approved',
    recipient: 'Customer',
    trigger: 'Admin approves trade application',
    icon: UserCheck,
    category: 'Users'
  },
  {
    slug: 'trade_rejected',
    name: 'Trade Application Rejected',
    description: 'Sent when a trade application is rejected',
    recipient: 'Customer',
    trigger: 'Admin rejects trade application',
    icon: UserCheck,
    category: 'Users'
  },
  {
    slug: 'welcome',
    name: 'Welcome Email',
    description: 'Sent to new users after registration',
    recipient: 'Customer',
    trigger: 'User registers',
    icon: Mail,
    category: 'Users'
  },
  {
    slug: 'purchase_order',
    name: 'Purchase Order',
    description: 'Sent to supplier when creating a PO',
    recipient: 'Supplier',
    trigger: 'Admin sends PO',
    icon: Package,
    category: 'Inventory'
  }
];

export default function AdminEmailSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Azure credentials state
  const [azureCredentials, setAzureCredentials] = useState({
    tenant_id: '',
    client_id: '',
    client_secret: '',
    email_sender: ''
  });
  const [azureSource, setAzureSource] = useState('');
  const [azureLoading, setAzureLoading] = useState(true);
  const [azureSaving, setAzureSaving] = useState(false);
  const [azureTesting, setAzureTesting] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  
  // Profile/Signature state
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    job_title: '',
    phone: '',
    email_signature: ''
  });
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  
  const token = localStorage.getItem('token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchSettings();
    fetchAzureCredentials();
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, authHeaders);
      setProfile({
        first_name: res.data.first_name || '',
        last_name: res.data.last_name || '',
        job_title: res.data.job_title || '',
        phone: res.data.phone || '',
        email_signature: res.data.email_signature || ''
      });
    } catch (err) {
      console.error('Failed to fetch profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, profile, authHeaders);
      toast.success('Profile saved successfully');
    } catch (err) {
      toast.error('Failed to save profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/email-settings`, authHeaders);
      setSettings(res.data.settings || {});
    } catch (err) {
      // If endpoint doesn't exist yet, use defaults (all enabled)
      const defaults = {};
      EMAIL_TYPES.forEach(type => {
        defaults[type.slug] = { enabled: true };
      });
      setSettings(defaults);
    } finally {
      setLoading(false);
    }
  };

  const fetchAzureCredentials = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings/azure`, authHeaders);
      setAzureCredentials({
        tenant_id: res.data.tenant_id || '',
        client_id: res.data.client_id || '',
        client_secret: res.data.client_secret || '',
        email_sender: res.data.email_sender || ''
      });
      setAzureSource(res.data.source || 'environment');
    } catch (err) {
      console.error('Failed to fetch Azure credentials', err);
    } finally {
      setAzureLoading(false);
    }
  };

  const handleAzureChange = (field, value) => {
    setAzureCredentials(prev => ({ ...prev, [field]: value }));
  };

  const saveAzureCredentials = async () => {
    setAzureSaving(true);
    try {
      await axios.put(`${API}/admin/settings/azure`, azureCredentials, authHeaders);
      toast.success('Azure credentials saved successfully');
      setAzureSource('database');
      // Refetch to get masked secret
      fetchAzureCredentials();
    } catch (err) {
      toast.error('Failed to save Azure credentials');
    } finally {
      setAzureSaving(false);
    }
  };

  const testAzureConnection = async () => {
    setAzureTesting(true);
    try {
      const res = await axios.post(`${API}/admin/settings/azure/test`, {}, authHeaders);
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error('Connection test failed');
    } finally {
      setAzureTesting(false);
    }
  };

  const handleToggle = async (slug, enabled) => {
    const newSettings = {
      ...settings,
      [slug]: { ...settings[slug], enabled }
    };
    setSettings(newSettings);
    
    try {
      await axios.put(`${API}/admin/email-settings`, { settings: newSettings }, authHeaders);
      toast.success(`${enabled ? 'Enabled' : 'Disabled'} ${EMAIL_TYPES.find(t => t.slug === slug)?.name}`);
    } catch (err) {
      toast.error('Failed to save settings');
      // Revert on error
      setSettings(settings);
    }
  };

  const categories = [...new Set(EMAIL_TYPES.map(t => t.category))];

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="email-settings-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Settings className="w-8 h-8 text-[#78BE20]" />
              Email Settings
            </h1>
            <p className="text-muted-foreground">Configure when and which emails are sent</p>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
          <div>
            <p className="text-sm text-blue-400 font-medium">Email Notifications Overview</p>
            <p className="text-sm text-blue-400/70 mt-1">
              Toggle emails on/off below. Disabled emails will not be sent even when their trigger occurs.
              To edit email content, go to <a href="/admin/email-templates" className="underline">Email Templates</a>.
            </p>
          </div>
        </div>

        {/* Loop Prevention Notice */}
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
          <div>
            <p className="text-sm text-amber-400 font-medium">Email Loop Prevention</p>
            <p className="text-sm text-amber-400/70 mt-1">
              <strong>Ticket notifications are loop-safe:</strong> They only trigger for manual admin actions (replies, status changes), 
              not for emails received via M365 sync. This prevents notification loops when customers reply.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : (
          <div className="space-y-8">
            {categories.map(category => (
              <div key={category}>
                <h2 className="text-lg font-semibold mb-4 text-muted-foreground">{category}</h2>
                <div className="grid gap-4">
                  {EMAIL_TYPES.filter(t => t.category === category).map(emailType => {
                    const Icon = emailType.icon;
                    const isEnabled = settings[emailType.slug]?.enabled !== false;
                    
                    return (
                      <Card key={emailType.slug} className={`bg-zinc-900/50 border-zinc-800 ${!isEnabled ? 'opacity-60' : ''}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-4">
                              <div className={`p-2 rounded-lg ${isEnabled ? 'bg-[#78BE20]/20' : 'bg-zinc-800'}`}>
                                <Icon className={`w-5 h-5 ${isEnabled ? 'text-[#78BE20]' : 'text-zinc-500'}`} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h3 className="font-medium">{emailType.name}</h3>
                                  <Badge variant="outline" className="text-xs">
                                    {emailType.recipient}
                                  </Badge>
                                  {emailType.loopSafe && (
                                    <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                                      Loop-Safe
                                    </Badge>
                                  )}
                                  {emailType.userPref && (
                                    <Badge className="bg-purple-500/20 text-purple-400 text-xs">
                                      User Opt-out
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground mt-1">{emailType.description}</p>
                                <p className="text-xs text-zinc-500 mt-2">
                                  <Send className="w-3 h-3 inline mr-1" />
                                  Trigger: {emailType.trigger}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Label htmlFor={`toggle-${emailType.slug}`} className="text-sm text-muted-foreground">
                                {isEnabled ? 'Enabled' : 'Disabled'}
                              </Label>
                              <Switch
                                id={`toggle-${emailType.slug}`}
                                checked={isEnabled}
                                onCheckedChange={(checked) => handleToggle(emailType.slug, checked)}
                                data-testid={`toggle-${emailType.slug}`}
                              />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Azure/M365 Credentials Section */}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="azure-credentials-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <Key className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <CardTitle className="text-lg">Microsoft 365 Email Credentials</CardTitle>
                  <CardDescription>
                    Configure Azure AD credentials for sending emails via Microsoft Graph API
                  </CardDescription>
                </div>
              </div>
              {azureSource && (
                <Badge variant="outline" className={azureSource === 'database' ? 'border-green-500 text-green-400' : 'border-yellow-500 text-yellow-400'}>
                  {azureSource === 'database' ? 'Custom' : 'Environment'}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {azureLoading ? (
              <div className="text-center py-4 text-muted-foreground">Loading credentials...</div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="tenant_id">Tenant ID</Label>
                    <Input
                      id="tenant_id"
                      data-testid="azure-tenant-id"
                      value={azureCredentials.tenant_id}
                      onChange={(e) => handleAzureChange('tenant_id', e.target.value)}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client_id">Client ID</Label>
                    <Input
                      id="client_id"
                      data-testid="azure-client-id"
                      value={azureCredentials.client_id}
                      onChange={(e) => handleAzureChange('client_id', e.target.value)}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client_secret">Client Secret</Label>
                    <div className="relative">
                      <Input
                        id="client_secret"
                        data-testid="azure-client-secret"
                        type={showSecret ? "text" : "password"}
                        value={azureCredentials.client_secret}
                        onChange={(e) => handleAzureChange('client_secret', e.target.value)}
                        placeholder="Enter new secret to update"
                        className="bg-zinc-800 border-zinc-700 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSecret(!showSecret)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
                      >
                        {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">Leave as is to keep existing secret</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email_sender">Sender Email Address</Label>
                    <Input
                      id="email_sender"
                      data-testid="azure-email-sender"
                      type="email"
                      value={azureCredentials.email_sender}
                      onChange={(e) => handleAzureChange('email_sender', e.target.value)}
                      placeholder="sales@yourdomain.com"
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                </div>
                
                <div className="flex items-center gap-3 pt-4 border-t border-zinc-800">
                  <Button 
                    onClick={saveAzureCredentials} 
                    disabled={azureSaving}
                    data-testid="save-azure-credentials"
                    className="bg-[#78BE20] hover:bg-[#6aab1c]"
                  >
                    {azureSaving ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Credentials
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={testAzureConnection}
                    disabled={azureTesting}
                    data-testid="test-azure-connection"
                  >
                    {azureTesting ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Test Connection
                  </Button>
                </div>

                <div className="bg-zinc-800/50 rounded-lg p-3 text-xs text-muted-foreground">
                  <p className="font-medium mb-1">How to get Azure credentials:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Go to <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Azure Portal</a> → Azure Active Directory → App registrations</li>
                    <li>Create or select your app registration</li>
                    <li>Copy the Application (client) ID and Directory (tenant) ID</li>
                    <li>Go to Certificates & secrets → New client secret</li>
                    <li>Ensure the app has Mail.Send permission in API permissions</li>
                  </ol>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Profile & Email Signature Section */}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="profile-signature-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#78BE20]/20">
                <Pen className="w-5 h-5 text-[#78BE20]" />
              </div>
              <div>
                <CardTitle className="text-lg">Email Signature & Profile</CardTitle>
                <CardDescription>
                  Your profile details are used to generate email signatures when sending from tickets
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {profileLoading ? (
              <div className="text-center py-4 text-muted-foreground">Loading profile...</div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      value={profile.first_name}
                      onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                      placeholder="Your first name"
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      value={profile.last_name}
                      onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                      placeholder="Your last name"
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="job_title">Job Title</Label>
                    <Input
                      id="job_title"
                      value={profile.job_title}
                      onChange={(e) => setProfile({ ...profile, job_title: e.target.value })}
                      placeholder="e.g. Sales Manager"
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number (for signature)</Label>
                    <Input
                      id="phone"
                      value={profile.phone}
                      onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                      placeholder="e.g. 1300 TECH DIST"
                      className="bg-zinc-800 border-zinc-700"
                    />
                    <p className="text-xs text-muted-foreground">Leave empty to use default: 1300 TECH DIST</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email_signature">Custom Email Signature (HTML)</Label>
                  <Textarea
                    id="email_signature"
                    value={profile.email_signature}
                    onChange={(e) => setProfile({ ...profile, email_signature: e.target.value })}
                    placeholder="Leave empty to use the default signature with your profile details above. Or paste custom HTML here."
                    className="bg-zinc-800 border-zinc-700 min-h-[100px] font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    If left empty, a professional signature will be auto-generated using your name, job title, and phone number.
                  </p>
                </div>
                
                <div className="flex items-center gap-3 pt-4 border-t border-zinc-800">
                  <Button 
                    onClick={saveProfile} 
                    disabled={profileSaving}
                    className="bg-[#78BE20] hover:bg-[#6aab1c]"
                  >
                    {profileSaving ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Profile
                  </Button>
                </div>

                {/* Signature Preview */}
                <div className="mt-4 p-4 bg-white rounded-lg">
                  <p className="text-xs text-zinc-500 mb-2">Signature Preview:</p>
                  <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '14px', color: '#333', borderTop: '2px solid #78BE20', paddingTop: '15px' }}>
                    <table cellPadding="0" cellSpacing="0" style={{ fontFamily: 'Arial, sans-serif' }}>
                      <tbody>
                        <tr>
                          <td style={{ paddingRight: '15px', borderRight: '2px solid #78BE20' }}>
                            <img src="https://customer-assets.emergentagent.com/job_6a45a73e-d7c9-4a2b-b47c-570b077eb490/artifacts/ddtl64n3_Logo%20-%20Transparent%20-%20Small.png" alt="Tech Distribution" style={{ width: '120px', height: 'auto' }} />
                          </td>
                          <td style={{ paddingLeft: '15px' }}>
                            <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#333' }}>
                              {profile.first_name || 'Your'} {profile.last_name || 'Name'}
                            </div>
                            {profile.job_title && (
                              <div style={{ color: '#666', fontSize: '13px' }}>{profile.job_title}</div>
                            )}
                            <div style={{ marginTop: '8px' }}>
                              <div style={{ fontWeight: 'bold', color: '#78BE20' }}>Tech Distribution Pty Ltd</div>
                              <div style={{ color: '#666', fontSize: '13px' }}>P: {profile.phone || '1300 TECH DIST'}</div>
                              <div style={{ color: '#666', fontSize: '13px' }}>E: sales@techdistribution.com.au</div>
                              <div style={{ color: '#666', fontSize: '13px' }}>W: www.techdistribution.com.au</div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Quick Links */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg">Related Settings</CardTitle>
          </CardHeader>
          <CardContent className="flex gap-4">
            <Button variant="outline" onClick={() => window.location.href = '/admin/email-templates'}>
              <Mail className="w-4 h-4 mr-2" />
              Edit Email Templates
            </Button>
            <Button variant="outline" onClick={() => window.location.href = '/admin/email-logs'}>
              <FileText className="w-4 h-4 mr-2" />
              View Email Logs
            </Button>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
